import type { TriageItemStatus } from '@signalvote/shared';

/**
 * Drives the ledger's one-shot "just completed" success wash. A row counts as
 * just-completed when it moved from `NeedsAction` to a terminal `Done` /
 * `Reported` state between two sort-inbox snapshots — the treasurer finished
 * the work (filled + confirmed, confirmed a clean row, or classified a bank
 * line), so the row settles into Done and we flash it so their eye keeps its
 * place instead of losing the row that just left the queue.
 *
 * Pure so the transition logic is unit-tested away from the render/timer wiring
 * (mirrors the deep-link `highlightId` mechanism, but keyed on a data
 * transition rather than a URL param). Returns the most-recent completion (one
 * row at a time in the triage flow) or null. A row absent from the prior
 * snapshot never flashes (initial load, a new page), and an uncheck
 * (`Done -> NeedsAction`) is not a completion.
 */
export function pickJustCompletedId(
  prevStatusById: Map<string, TriageItemStatus>,
  items: ReadonlyArray<{ id: string; status: TriageItemStatus }>,
): string | null {
  let completed: string | null = null;
  for (const item of items) {
    const prev = prevStatusById.get(item.id);
    const nowDone = item.status === 'Done' || item.status === 'Reported';
    if (prev === 'NeedsAction' && nowDone) {
      completed = item.id;
    }
  }
  return completed;
}

/** Snapshot the current `(id -> status)` for the next transition diff. */
export function snapshotStatuses(
  items: ReadonlyArray<{ id: string; status: TriageItemStatus }>,
): Map<string, TriageItemStatus> {
  const next = new Map<string, TriageItemStatus>();
  for (const item of items) next.set(item.id, item.status);
  return next;
}

/**
 * Resolves what to do with the row a classify just produced, once it appears in
 * the refetched inbox. Classifying a bank line replaces it with a new entity
 * (different id); if that entity STILL needs work (an over-limit expenditure
 * missing its payee address, an itemized contribution missing donor info) the
 * treasurer should land straight in its editor rather than watch the row
 * collapse and have to hunt for it and click back in. If it came out Done
 * (below threshold), we leave it collapsed so the completion wash reads.
 *
 * `resolved: false` means the new row hasn't arrived in `items` yet (the refetch
 * is in flight) so the caller keeps waiting; `resolved: true` means act on
 * `expandId` (the new row's id to open, or null to leave collapsed) and clear
 * the pending follow-up.
 */
export function resolveClassifyFollowUp(
  followUpId: string,
  items: ReadonlyArray<{ id: string; status: TriageItemStatus }>,
): { resolved: boolean; expandId: string | null } {
  const row = items.find((it) => it.id === followUpId);
  if (!row) return { resolved: false, expandId: null };
  return {
    resolved: true,
    expandId: row.status === 'NeedsAction' ? followUpId : null,
  };
}
